import React, { useEffect, useState } from 'react';
import toSentenceCase from '../../../services/toSentenceCase';
import Inputs from 'components/Inputs';
import Typography from 'kingpin/atoms/Typography';
import Row from '../../Common/Row';

export const getComparisonLabel = (comparison?: PersistedComparisonType) => {
  if (!comparison) {
    return 'None';
  }

  if (comparison.compareType === 'previous') {
    return 'Previous Period';
  }

  if (comparison.compareType === 'lastYear') {
    return 'Same Period - Previous Year';
  }

  if (
    comparison.compareType === 'compareTo' &&
    !!comparison.relativeDateOption
  ) {
    return toSentenceCase(comparison.relativeDateOption);
  }

  return 'unknown';
};

type ComparisonOption = {
  label: string;
  value: PersistedComparisonType | undefined;
};

const SINGLE_METRIC_DATE_MATRIX_COMPARISON_OPTIONS: ComparisonOption[] = [
  {
    value: {
      compareType: 'compareTo',
      relativeDateOption: 'last 7 days',
    },
    label: 'Previous 7 days',
  },
  {
    value: {
      compareType: 'compareTo',
      relativeDateOption: 'last 14 days',
    },
    label: 'Previous 14 days',
  },
  {
    value: {
      compareType: 'compareTo',
      relativeDateOption: 'last 30 days',
    },
    label: 'Previous 30 days',
  },
  {
    value: {
      compareType: 'compareTo',
      relativeDateOption: 'last 60 days',
    },
    label: 'Previous 60 days',
  },
  {
    value: {
      compareType: 'compareTo',
      relativeDateOption: 'last 90 days',
    },
    label: 'Previous 90 days',
  },
  {
    value: {
      compareType: 'compareTo',
      relativeDateOption: 'last 180 days',
    },
    label: 'Previous 180 days',
  },
  {
    value: {
      compareType: 'compareTo',
      relativeDateOption: 'last 365 days',
    },
    label: 'Previous 365 days',
  },
  {
    value: {
      compareType: 'compareTo',
      relativeDateOption: 'last week',
    },
    label: 'Previous week',
  },
  {
    value: {
      compareType: 'compareTo',
      relativeDateOption: 'last month',
    },
    label: 'Previous month',
  },
  {
    value: {
      compareType: 'compareTo',
      relativeDateOption: 'last year',
    },
    label: 'Previous year',
  },
];

const PACE_COMPARISON_OPTIONS: ComparisonOption[] = [
  {
    value: {
      compareType: 'compareTo',
      relativeDateOption: 'last week',
    },
    label: 'Last week',
  },
  {
    value: {
      compareType: 'compareTo',
      relativeDateOption: 'last 4 weeks',
    },
    label: 'Last 4 weeks',
  },
  {
    value: {
      compareType: 'compareTo',
      relativeDateOption: 'last 13 weeks',
    },
    label: 'Last 13 weeks',
  },
];

const buildOptions = ({
  hasPace,
  hasSingleMetricDateMatrix,
  isOnlyPreviousAllowed,
  isPaceForm,
  isSingleMetricDateMatrixForm,
}: {
  hasPace: boolean;
  hasSingleMetricDateMatrix: boolean;
  isOnlyPreviousAllowed: boolean;
  isPaceForm?: boolean;
  isSingleMetricDateMatrixForm?: boolean;
}): ComparisonOption[] => {
  if (isPaceForm) {
    return PACE_COMPARISON_OPTIONS;
  }

  if (isSingleMetricDateMatrixForm) {
    return SINGLE_METRIC_DATE_MATRIX_COMPARISON_OPTIONS;
  }

  if (hasPace && hasSingleMetricDateMatrix) {
    return [
      ...PACE_COMPARISON_OPTIONS,
      ...SINGLE_METRIC_DATE_MATRIX_COMPARISON_OPTIONS,
    ];
  }

  if (hasPace) {
    return PACE_COMPARISON_OPTIONS;
  }

  if (hasSingleMetricDateMatrix) {
    return SINGLE_METRIC_DATE_MATRIX_COMPARISON_OPTIONS;
  }

  const options: ComparisonOption[] = [
    {
      value: undefined,
      label: 'None',
    },
    {
      value: {
        compareType: 'previous',
      },
      label: 'Previous Period',
    },
  ];

  if (!isOnlyPreviousAllowed) {
    options.push({
      value: { compareType: 'lastYear' },
      label: 'Same Period - Previous Year',
    });
  }

  return options;
};

const ComparisonSelector = ({
  currentComparison,
  setCurrentComparison,
  track,
  hasPace,
  hasSingleMetricDateMatrix,
  isOnlyPreviousAllowed,
  isPaceForm,
  isSingleMetricDateMatrixForm,
}: {
  currentComparison?: PersistedComparisonType;
  setCurrentComparison: (c: PersistedComparisonType | undefined) => void;
  track: () => void;
  hasPace: boolean;
  hasSingleMetricDateMatrix: boolean;
  isOnlyPreviousAllowed: boolean;
  isPaceForm?: boolean;
  isSingleMetricDateMatrixForm?: boolean;
}) => {
  const [options, setOptions] = useState<DropdownOption[]>([]);
  const [selectedLabel, setSelectedLabel] = useState<string | undefined>();

  useEffect(() => {
    const selectedOption = options.find((o) => o.isSelected);
    if (selectedOption) {
      setSelectedLabel(selectedOption.label);
      return;
    }
    setSelectedLabel(undefined);
  }, [options]);

  useEffect(() => {
    setOptions(
      buildOptions({
        hasPace,
        hasSingleMetricDateMatrix,
        isOnlyPreviousAllowed,
        isPaceForm,
        isSingleMetricDateMatrixForm,
      }).map((o) => {
        const isSelected = (() => {
          if (currentComparison === undefined) {
            return false;
          }
          if (o.value === undefined) {
            return false;
          }
          return (
            o.value.compareType === currentComparison.compareType &&
            o.value.relativeDateOption === currentComparison.relativeDateOption
          );
        })();
        return {
          label: o.label,
          isSelected,
          onSelected: () => {
            setCurrentComparison(o.value);
            track();
          },
        };
      }),
    );
  }, [
    currentComparison,
    hasPace,
    hasSingleMetricDateMatrix,
    isOnlyPreviousAllowed,
    isPaceForm,
    isSingleMetricDateMatrixForm,
    setCurrentComparison,
    track,
  ]);

  return (
    <Inputs.Dropdown
      options={options}
      placeholder="Select a comparison"
      renderSelectedLabel={
        selectedLabel
          ? () => (
              <Row centerAlign>
                <div style={{ marginRight: 4, display: 'flex' }}>
                  <Typography.Body type={'Placeholder'}>
                    Comparison:
                  </Typography.Body>
                </div>
                <Typography.Body type={'Label'}>
                  {selectedLabel}
                </Typography.Body>
              </Row>
            )
          : undefined
      }
    />
  );
};

export default ComparisonSelector;
